import store from "../store";

/**
 * WebSocket Service for handling real-time communication
 */
class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 3000; // 3 seconds
    this.reconnectTimer = null;
    this.messageHandlers = new Map();
  }

  /**
   * Initialize WebSocket connection
   * @returns {Promise} Promise that resolves when connection is established
   */
  connect() {
    console.log("WebSocketService.connect()");
    return new Promise((resolve, reject) => {
      if (this.socket && this.isConnected) {
        resolve();
        return;
      }

      // Get the current auth token from user store
      const token = store.getters["user/userToken"];

      if (!token) {
        reject(new Error("Authentication token not found"));
        return;
      }

      // Determine WebSocket URL (use secure WebSocket if on HTTPS)
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const host = process.env.VUE_APP_API_URL
        ? process.env.VUE_APP_API_URL.replace(/^https?:\/\//, "")
        : window.location.host;

      const wsUrl = `${protocol}//${host}/ws/connect?token=${token}`;
      try {
        this.socket = new WebSocket(wsUrl);

        this.socket.onopen = () => {
          console.log("WebSocket connection established");
          this.isConnected = true;
          this.reconnectAttempts = 0;
          resolve();

          // Dispatch connection status to store if available
          if (store && store.dispatch) {
            store.dispatch("websocket/setConnectionStatus", true);
          }
        };

        this.socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.debug("WebSocket message received:", data);

            // Handle connection established message
            if (data.type === "connection_established") {
              console.log(
                `WebSocket connection confirmed for user ${data.userId}`
              );
            }

            // Dispatch message to store
            if (store && store.dispatch) {
              store.dispatch("websocket/messageReceived", data);
            }

            // Call registered message handlers
            if (data.type && this.messageHandlers.has(data.type)) {
              this.messageHandlers.get(data.type).forEach((handler) => {
                try {
                  handler(data);
                } catch (handlerError) {
                  console.error(
                    "Error in WebSocket message handler:",
                    handlerError
                  );
                }
              });
            }
            if (data.event && this.messageHandlers.has(data.event)) {
              this.messageHandlers.get(data.event).forEach((handler) => {
                try {
                  handler(data);
                } catch (handlerError) {
                  console.error(
                    "Error in WebSocket message handler:",
                    handlerError
                  );
                }
              });
            }
          } catch (error) {
            console.error("Error parsing WebSocket message:", error);
          }
        };

        this.socket.onclose = (event) => {
          this.isConnected = false;
          console.log(
            `WebSocket connection closed: ${event.code} ${event.reason}`
          );

          // Dispatch connection status to store
          if (store && store.dispatch) {
            store.dispatch("websocket/setConnectionStatus", false);
          }

          // Attempt to reconnect if not closed intentionally
          if (event.code !== 1000) {
            this.attemptReconnect();
          }
        };

        this.socket.onerror = (error) => {
          console.error("WebSocket error:", error);
          reject(error);
        };
      } catch (error) {
        console.error("Failed to create WebSocket connection:", error);
        reject(error);
      }
    });
  }

  /**
   * Attempt to reconnect to WebSocket server
   */
  attemptReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(
        `Attempting to reconnect WebSocket (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`
      );

      this.reconnectTimer = setTimeout(() => {
        this.connect().catch((error) => {
          console.error("WebSocket reconnection failed:", error);
        });
      }, this.reconnectInterval);
    } else {
      console.error("Maximum WebSocket reconnection attempts reached");
    }
  }

  /**
   * Disconnect WebSocket connection
   */
  disconnect() {
    if (this.socket && this.isConnected) {
      this.socket.close(1000, "User logout");
      this.isConnected = false;

      // Clear reconnect timer if exists
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }

      console.log("WebSocket disconnected");
    }
  }

  /**
   * Send message through WebSocket
   * @param {Object} message - Message to send
   * @returns {boolean} - True if message was sent
   */
  sendMessage(message) {
    if (!this.socket || !this.isConnected) {
      console.warn("Cannot send message: WebSocket not connected");
      return false;
    }

    try {
      this.socket.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error("Error sending WebSocket message:", error);
      return false;
    }
  }

  /**
   * Register a handler for specific message types
   * @param {string} messageType - Type of message to handle
   * @param {Function} handler - Handler function
   */
  registerMessageHandler(messageType, handler) {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, new Set());
    }
    this.messageHandlers.get(messageType).add(handler);
  }

  /**
   * Unregister a message handler
   * @param {string} messageType - Type of message
   * @param {Function} handler - Handler function to remove
   */
  unregisterMessageHandler(messageType, handler) {
    if (this.messageHandlers.has(messageType)) {
      this.messageHandlers.get(messageType).delete(handler);
    }
  }

  /**
   * Check if WebSocket is connected
   * @returns {boolean} - Connection status
   */
  isWebSocketConnected() {
    return this.isConnected;
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService;
